// src/store/canvas.js
// Central state for the label canvas:
//   elements, selection, drag/resize, undo/redo history, zoom

import { v4 as uuid } from 'uuid'
import { clearTextWidthCache } from '@/utils/canvasRenderer.js'

const MAX_HISTORY = 50

// Fields stored as DECIMAL/INT in the database. MySQL returns DECIMAL columns
// as strings by default; if we don't coerce, arithmetic in drag/resize handlers
// will silently concatenate strings and produce NaN, losing the element.
const NUMERIC_FIELDS = [
  'labelX', 'labelY',
  'elementWidth', 'elementHeight',
  'fontSize', 'font_weight', 'rotation',
  'daysOffset',
  'qrSize',
  'barcodeWidth', 'barcodeHeight',
  'valueX', 'valueY', 'valueFontSize',
  'allergenIconSize', 'allergenSpacing',
  'lineWidth', 'lineThickness',
  'maxLines', 'zIndex', 'fieldOrder',
]

function coerceNumeric(el) {
  const out = { ...el }
  for (const f of NUMERIC_FIELDS) {
    if (out[f] !== null && out[f] !== undefined && out[f] !== '') {
      const n = Number(out[f])
      if (Number.isFinite(n)) out[f] = n
    }
  }
  return out
}

function cloneElements(elements) {
  return JSON.parse(JSON.stringify(elements))
}

export default {
  namespaced: true,

  state: () => ({
    elements:        [],        // Array of element objects on canvas
    selectedIds:     [],        // Array of selected elementId/tempId
    history:         [],        // Array of past element snapshots (undo stack)
    future:          [],        // Array of future snapshots (redo stack)
    zoom:            1.0,       // Canvas zoom level (0.25 – 4.0)
    gridEnabled:     true,      // Snap to grid
    gridSizeMm:      1,         // Grid size in mm
    dirty:           false,     // Unsaved changes flag
    previewData:     {},        // token → real value for preview mode
    previewMode:     false,     // Show real data on canvas
  }),

  getters: {
    elements:      (s) => s.elements,
    selectedIds:   (s) => s.selectedIds,
    selectedElements: (s) => s.elements.filter(e =>
      s.selectedIds.includes(e.tempId ?? e.elementId)
    ),
    firstSelected: (s, g) => g.selectedElements[0] || null,
    zoom:          (s) => s.zoom,
    gridEnabled:   (s) => s.gridEnabled,
    gridSizeMm:    (s) => s.gridSizeMm,
    dirty:         (s) => s.dirty,
    previewMode:   (s) => s.previewMode,
    previewData:   (s) => s.previewData,
    canUndo:       (s) => s.history.length > 0,
    canRedo:       (s) => s.future.length  > 0,

    // Returns the display value for an element token (preview or placeholder)
    displayValue: (s) => (element) => {
      if (!s.previewMode) {
        return element.fieldLabel || `{${element.databaseField || element.fieldType}}`
      }
      const token = element.databaseField || element.qrDataField || element.barcodeDataField
      return token ? (s.previewData[token] ?? `{${token}}`) : ''
    },
  },

  mutations: {
    // ── Elements ───────────────────────────────────────────
    SET_ELEMENTS(state, elements) {
      state.elements = elements.map(e => {
        const coerced = coerceNumeric(e)
        return {
          ...coerced,
          tempId: coerced.tempId || coerced.elementId?.toString() || uuid(),
        }
      })
      state.dirty = false
    },

    ADD_ELEMENT(state, element) {
      const coerced = coerceNumeric(element)
      const el = { ...coerced, tempId: coerced.tempId || uuid() }
      state.elements.push(el)
      state.dirty = true
    },

    UPDATE_ELEMENT(state, { tempId, changes }) {
      const idx = state.elements.findIndex(e => e.tempId === tempId)
      if (idx !== -1) {
        // Coerce any numeric fields in the incoming changes — guards against
        // form inputs or other sources writing string values.
        const coercedChanges = coerceNumeric(changes)
        state.elements.splice(idx, 1, { ...state.elements[idx], ...coercedChanges })
        state.dirty = true
      }
    },

    REMOVE_ELEMENT(state, tempId) {
      state.elements = state.elements.filter(e => e.tempId !== tempId)
      state.selectedIds = state.selectedIds.filter(id => id !== tempId)
      state.dirty = true
    },

    REMOVE_SELECTED(state) {
      state.elements = state.elements.filter(
        e => !state.selectedIds.includes(e.tempId)
      )
      state.selectedIds = []
      state.dirty = true
    },

    // ── Selection ──────────────────────────────────────────
    SET_SELECTION(state, ids) {
      state.selectedIds = Array.isArray(ids) ? ids : [ids]
    },
    ADD_TO_SELECTION(state, id) {
      if (!state.selectedIds.includes(id)) state.selectedIds.push(id)
    },
    CLEAR_SELECTION(state) {
      state.selectedIds = []
    },

    // ── Layer order ────────────────────────────────────────
    REORDER_ELEMENTS(state, orderedTempIds) {
      const map = {}
      state.elements.forEach(e => { map[e.tempId] = e })
      const reordered = orderedTempIds.map(id => map[id]).filter(Boolean)
      state.elements = reordered
    },

    BRING_FORWARD(state, tempId) {
      const idx = state.elements.findIndex(e => e.tempId === tempId)
      if (idx < state.elements.length - 1) {
        const arr = [...state.elements]
        ;[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]
        arr.forEach((e, i) => { e.zIndex = i })
        state.elements = arr
        state.dirty = true
      }
    },
    SEND_BACKWARD(state, tempId) {
      const idx = state.elements.findIndex(e => e.tempId === tempId)
      if (idx > 0) {
        const arr = [...state.elements]
        ;[arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]]
        arr.forEach((e, i) => { e.zIndex = i })
        state.elements = arr
        state.dirty = true
      }
    },
    BRING_TO_FRONT(state, tempId) {
      const arr   = [...state.elements]
      const [el]  = arr.splice(arr.findIndex(e => e.tempId === tempId), 1)
      arr.push(el)
      arr.forEach((e, i) => { e.zIndex = i })
      state.elements = arr
      state.dirty = true
    },
    SEND_TO_BACK(state, tempId) {
      const arr  = [...state.elements]
      const [el] = arr.splice(arr.findIndex(e => e.tempId === tempId), 1)
      arr.unshift(el)
      arr.forEach((e, i) => { e.zIndex = i })
      state.elements = arr
      state.dirty = true
    },

    // ── History ────────────────────────────────────────────
    PUSH_HISTORY(state) {
      state.history.push(cloneElements(state.elements))
      if (state.history.length > MAX_HISTORY) state.history.shift()
      state.future = []
    },
    UNDO(state) {
      if (state.history.length === 0) return
      state.future.push(cloneElements(state.elements))
      state.elements = state.history.pop()
      state.dirty    = true
    },
    REDO(state) {
      if (state.future.length === 0) return
      state.history.push(cloneElements(state.elements))
      state.elements = state.future.pop()
      state.dirty    = true
    },

    // ── Canvas settings ────────────────────────────────────
    SET_ZOOM(state, zoom) {
      state.zoom = Math.max(0.25, Math.min(4.0, zoom))
    },
    SET_GRID(state, { enabled, sizeMm }) {
      if (enabled  !== undefined) state.gridEnabled = enabled
      if (sizeMm   !== undefined) state.gridSizeMm  = sizeMm
    },
    SET_DIRTY(state, val)        { state.dirty       = val },

    // ── Preview ────────────────────────────────────────────
    SET_PREVIEW_MODE(state, val)  { state.previewMode = val },
    SET_PREVIEW_DATA(state, data) { state.previewData = data },
  },

  actions: {
    loadElements({ commit }, elements) {
      commit('SET_ELEMENTS', elements || [])
    },

    addElement({ commit }, element) {
      commit('PUSH_HISTORY')
      commit('ADD_ELEMENT', element)
      commit('SET_SELECTION', [element.tempId || element.elementId?.toString()])
    },

    updateElement({ commit }, payload) {
      commit('UPDATE_ELEMENT', payload)
    },

    updateElementWithHistory({ commit }, payload) {
      commit('PUSH_HISTORY')
      commit('UPDATE_ELEMENT', payload)
    },

    removeSelected({ commit }) {
      commit('PUSH_HISTORY')
      commit('REMOVE_SELECTED')
    },

    removeElement({ commit }, tempId) {
      commit('PUSH_HISTORY')
      commit('REMOVE_ELEMENT', tempId)
    },

    undo({ commit }) { commit('UNDO') },
    redo({ commit }) { commit('REDO') },

    setZoom({ commit }, zoom) { commit('SET_ZOOM', zoom) },

    zoomIn({ commit, state }) {
      const steps = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4]
      const next  = steps.find(s => s > state.zoom) || 4
      commit('SET_ZOOM', next)
    },
    zoomOut({ commit, state }) {
      const steps = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4]
      const prev  = [...steps].reverse().find(s => s < state.zoom) || 0.25
      commit('SET_ZOOM', prev)
    },

    toggleGrid({ commit, state }) {
      commit('SET_GRID', { enabled: !state.gridEnabled })
    },

    setPreviewMode({ commit }, { enabled, data = {} }) {
      commit('SET_PREVIEW_MODE', enabled)
      if (enabled) {
        commit('SET_PREVIEW_DATA', data)
        clearTextWidthCache()
      } else {
        clearTextWidthCache()
      }
    },

    // Serialise elements for API save (strip tempId, set fieldOrder/zIndex)
    serialiseForSave({ state }) {
      return state.elements.map((el, idx) => {
        const { tempId, ...rest } = el
        return { ...rest, fieldOrder: idx, zIndex: idx }
      })
    },
  },
}